import insightsData from '../components/Insights/insightsData';
import metadataConfig from './routeMetadata.config.json';
import { buildCanonical, normalizeRoutePath } from './metaRuntime';

function compactWhitespace(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function toIsoDate(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString();
}

function cloneSerializable(value) {
  return JSON.parse(JSON.stringify(value));
}

function makeOrganizationNode() {
  const schema = metadataConfig.schema || {};
  const organization = schema.organization || {};
  const canonicalOrigin = compactWhitespace(organization.url || 'https://www.douglasmccartney.com');
  const logoPath = compactWhitespace(organization.logoPath || '/d_logo.png');
  const node = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: compactWhitespace(organization.name || metadataConfig.brand || 'Douglas & McCartney'),
    url: canonicalOrigin,
    logo: buildCanonical(logoPath, { canonicalOrigin }),
    description: compactWhitespace(organization.description),
    areaServed: organization.areaServed || undefined,
    sameAs: Array.isArray(organization.sameAs) ? organization.sameAs.filter(Boolean) : undefined,
  };

  Object.keys(node).forEach((key) => {
    if (node[key] == null || node[key] === '' || (Array.isArray(node[key]) && node[key].length === 0)) {
      delete node[key];
    }
  });

  return node;
}

function makeWebSiteNode(organizationNode) {
  const schema = metadataConfig.schema || {};
  const website = schema.website || {};
  const url = compactWhitespace(website.url || organizationNode.url || 'https://www.douglasmccartney.com');
  const node = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: compactWhitespace(website.name || metadataConfig.brand || 'Douglas & McCartney'),
    url,
    inLanguage: compactWhitespace(website.inLanguage || 'en-IN'),
    description: compactWhitespace(website.description || ''),
    publisher: {
      '@type': 'Organization',
      name: organizationNode.name,
      url: organizationNode.url,
    },
  };

  Object.keys(node).forEach((key) => {
    if (node[key] == null || node[key] === '') delete node[key];
  });

  return node;
}

function makeServiceNode(pathname, meta, organizationNode) {
  const schemaConfig = metadataConfig.schema || {};
  const serviceConfig = schemaConfig.servicePages?.[pathname];
  if (!serviceConfig || !meta?.title || !meta?.description || !meta?.canonical) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: compactWhitespace(serviceConfig.name || meta.title),
    description: compactWhitespace(meta.description),
    url: compactWhitespace(meta.canonical),
    serviceType: compactWhitespace(serviceConfig.serviceType || meta.title),
    areaServed: serviceConfig.areaServed || schemaConfig.organization?.areaServed || 'India',
    provider: {
      '@type': 'Organization',
      name: organizationNode.name,
      url: organizationNode.url,
    },
  };
}

function makeArticleNode(post, meta, organizationNode) {
  if (!post || !meta?.canonical || !meta?.title || !meta?.description) return null;
  const publishedIso = toIsoDate(post.date);
  const node = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: compactWhitespace(post.title || meta.title),
    description: compactWhitespace(meta.description),
    url: compactWhitespace(meta.canonical),
    mainEntityOfPage: compactWhitespace(meta.canonical),
    datePublished: publishedIso || undefined,
    dateModified: publishedIso || undefined,
    articleSection: compactWhitespace(post.category),
    inLanguage: 'en-IN',
    author: {
      '@type': 'Organization',
      name: organizationNode.name,
      url: organizationNode.url,
    },
    publisher: {
      '@type': 'Organization',
      name: organizationNode.name,
      url: organizationNode.url,
      logo: organizationNode.logo
        ? {
            '@type': 'ImageObject',
            url: organizationNode.logo,
          }
        : undefined,
    },
  };

  if (node.publisher && !node.publisher.logo) {
    delete node.publisher.logo;
  }

  Object.keys(node).forEach((key) => {
    if (node[key] == null || node[key] === '') delete node[key];
  });
  return node;
}

function makeBreadcrumbNode(items) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: compactWhitespace(item.name),
      item: compactWhitespace(item.item),
    })),
  };
}

function makeLocalBusinessNode(pathname) {
  const cfg = metadataConfig.schema?.localBusiness || {};
  if (!cfg.enabled) return null;
  if (cfg.route && normalizeRoutePath(cfg.route) !== normalizeRoutePath(pathname)) return null;
  if (!cfg.name || !cfg.address) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: compactWhitespace(cfg.name),
    url: buildCanonical(cfg.url || pathname),
    description: compactWhitespace(cfg.description || ''),
    telephone: compactWhitespace(cfg.telephone || ''),
    email: compactWhitespace(cfg.email || ''),
    areaServed: cfg.areaServed || undefined,
    address: cloneSerializable(cfg.address),
  };
}

function makeFAQPageNode(pathname) {
  const routeMap = metadataConfig.schema?.faqPages?.routes || {};
  const entry = routeMap[pathname];
  if (!entry || !Array.isArray(entry.questions) || entry.questions.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: entry.questions
      .filter((q) => q && q.question && q.answer)
      .map((q) => ({
        '@type': 'Question',
        name: compactWhitespace(q.question),
        acceptedAnswer: {
          '@type': 'Answer',
          text: compactWhitespace(q.answer),
        },
      })),
  };
}

function getConfiguredBreadcrumbItems(pathname, meta) {
  const routeCrumbs = metadataConfig.schema?.breadcrumbs?.routes || {};
  const direct = routeCrumbs[pathname];
  if (Array.isArray(direct) && direct.length) {
    return direct.map((crumb) => ({
      name: crumb.name,
      item: buildCanonical(crumb.path || '/'),
    }));
  }

  const insightMatch = pathname.match(/^\/insights\/([^/]+)$/);
  if (insightMatch && metadataConfig.schema?.breadcrumbs?.insightsPost === true && meta?.title) {
    return [
      { name: 'Home', item: buildCanonical('/') },
      { name: 'Insights', item: buildCanonical('/insights') },
      { name: meta.title, item: meta.canonical },
    ];
  }

  return null;
}

export function resolveRouteSchemaDocuments({ pathname = '/', meta = null } = {}) {
  const path = normalizeRoutePath(pathname);
  const results = [];

  if (!meta || !meta.indexable || !meta.canonical) {
    return results;
  }

  const organizationNode = makeOrganizationNode();
  const webSiteNode = makeWebSiteNode(organizationNode);
  results.push({ key: 'site-organization', node: organizationNode });
  results.push({ key: 'site-website', node: webSiteNode });

  const serviceNode = makeServiceNode(path, meta, organizationNode);
  if (serviceNode) {
    results.push({ key: `service:${path}`, node: serviceNode });
  }

  const insightMatch = path.match(/^\/insights\/([^/]+)$/);
  if (insightMatch) {
    const post = insightsData.find((item) => item.slug === insightMatch[1]);
    const articleNode = makeArticleNode(post, meta, organizationNode);
    if (articleNode) {
      results.push({ key: `article:${insightMatch[1]}`, node: articleNode });
    }
  }

  const breadcrumbItems = getConfiguredBreadcrumbItems(path, meta);
  const breadcrumbNode = makeBreadcrumbNode(breadcrumbItems);
  if (breadcrumbNode) {
    results.push({ key: `breadcrumbs:${path}`, node: breadcrumbNode });
  }

  const localBusinessNode = makeLocalBusinessNode(path);
  if (localBusinessNode) {
    results.push({ key: `localbusiness:${path}`, node: localBusinessNode });
  }

  const faqNode = makeFAQPageNode(path);
  if (faqNode) {
    results.push({ key: `faq:${path}`, node: faqNode });
  }

  return results;
}

function ensureJsonLdScript(key, payload) {
  if (typeof document === 'undefined') return;
  const head = document.head || document.getElementsByTagName('head')[0];
  if (!head) return;
  const selector = `script[type="application/ld+json"][data-seo-jsonld-key="${key}"]`;
  let node = head.querySelector(selector);
  if (!node) {
    node = document.createElement('script');
    node.type = 'application/ld+json';
    node.setAttribute('data-seo-jsonld', 'true');
    node.setAttribute('data-seo-jsonld-key', key);
    head.appendChild(node);
  }
  const nextText = JSON.stringify(payload);
  if (node.textContent !== nextText) {
    node.textContent = nextText;
  }
}

export function applyDocumentSchemas(documents) {
  if (typeof document === 'undefined') return;
  const nextDocs = Array.isArray(documents) ? documents : [];
  const keys = new Set(nextDocs.map((doc) => doc.key));
  const existing = document.querySelectorAll('script[type="application/ld+json"][data-seo-jsonld="true"]');
  existing.forEach((node) => {
    const key = node.getAttribute('data-seo-jsonld-key');
    if (!keys.has(key) && node.parentNode) {
      node.parentNode.removeChild(node);
    }
  });

  nextDocs.forEach((doc) => {
    if (!doc || !doc.key || !doc.node) return;
    ensureJsonLdScript(doc.key, doc.node);
  });
}
